import { useSyncExternalStore } from 'react';
import type { Lang } from '@polyglot/engine';

/**
 * Per-device preferences. Browser storage is only a convenience here: every access is guarded
 * and the app works with defaults when storage is unavailable (private mode, blocked data...).
 */
export type UiLang = 'fr' | 'en' | 'es' | 'de' | 'it' | 'pt';
export const UI_LANGS: UiLang[] = ['fr', 'en', 'es', 'de', 'it', 'pt'];

export interface Settings {
  uiLang: UiLang;
  sound: boolean;
  haptics: boolean;
  motion: 'system' | 'reduced' | 'full';
  /** Live validity hints, only honoured in Relax mode (never in competitive rounds). */
  relaxHints: boolean;
  lastLanguages: Lang[];
  firstGameDone: boolean;
}

const KEY = 'pa.settings.v1';

const DEFAULTS: Settings = {
  uiLang: 'fr',
  sound: true,
  haptics: true,
  motion: 'system',
  relaxHints: true,
  lastLanguages: ['fr'],
  firstGameDone: false,
};

function read(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Settings>) };
  } catch {
    /* ignore */
  }
  // French interface by default (product decision); the player can switch at any time.
  return { ...DEFAULTS };
}

let current: Settings = read();
const listeners = new Set<() => void>();

export function getSettings(): Settings {
  return current;
}

export function updateSettings(patch: Partial<Settings>): void {
  current = { ...current, ...patch };
  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    /* ignore */
  }
  applyMotion();
  for (const l of listeners) l();
}

export function useSettings(): Settings {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => current,
  );
}

export function reducedMotion(): boolean {
  if (current.motion === 'reduced') return true;
  if (current.motion === 'full') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function applyMotion(): void {
  document.documentElement.dataset.motion = reducedMotion() ? 'reduced' : 'full';
}
